import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { SessionState, Language, LocalUser, JoisstApiUser } from '../types';
import { DEFAULT_LANGUAGE } from '../constants';
import { t } from '../services/sessionManager';

declare global {
  interface Window {
    Joisst?: any;
  }
}

const getInitialJoisstUser = () => {
  if (typeof window !== 'undefined' && window.Joisst && window.Joisst.user) {
    return window.Joisst.user;
  }
  return {
    userId: 'anon',
    id: 'anon',
    hearts: 1,
    brains: 0,
    language: 'en',
    theme: 'light',
    isAnonymous: true,
    referralCode: ''
  };
};

interface SessionContextValue {
  user: LocalUser | null;
  apiData: JoisstApiUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  language: Language;
  themeNotice: string | null;
  debugMessage?: string;
  refreshSession: () => Promise<void>;
  login: () => void;
  logout: () => void;
  setLanguage: (lang: Language) => void;
  toggleTheme: () => void;
  acceptCookies: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [joisstUser, setJoisstUser] = useState<any>(getInitialJoisstUser);
  const [themeNotice, setThemeNotice] = useState<string | null>(null);

  useEffect(() => {
    // Sync initial state if Joisst loaded after context initialization
    if (window.Joisst) {
      setJoisstUser({ ...window.Joisst.user });
    }

    const handleUserChanged = (e: any) => {
      setJoisstUser({ ...e.detail });
    };

    const handleSessionChanged = (e: any) => {
      // Definitive Session Lifecycle Signal
      // "When this event fires (indicating a user has logged in, logged out, or switched accounts), you must treat this as the signal to immediately unload the previous user's cached UI state or progress and load the saved records corresponding to the new Joisst.user.userId."
      setJoisstUser({ ...e.detail });
      console.log('Joisst session lifecycle changed:', e.detail);
    };

    const handleThemeChanged = (e: any) => {
      const theme = e.detail;
      const themeLabel = t(theme as any, (joisstUser.language || 'en') as Language);
      setThemeNotice(`${t('themeSwitched', (joisstUser.language || 'en') as Language)} ${themeLabel.toUpperCase()}`);
      setTimeout(() => setThemeNotice(null), 2000);
    };

    window.addEventListener('joisst-user-changed', handleUserChanged);
    window.addEventListener('joisst-session-changed', handleSessionChanged);
    window.addEventListener('joisst-theme-changed', handleThemeChanged);

    return () => {
      window.removeEventListener('joisst-user-changed', handleUserChanged);
      window.removeEventListener('joisst-session-changed', handleSessionChanged);
      window.removeEventListener('joisst-theme-changed', handleThemeChanged);
    };
  }, [joisstUser.language]);

  const setLanguage = (lang: Language) => {
    if (window.Joisst) {
      window.Joisst.updateUser({ language: lang });
    }
  };

  const toggleTheme = () => {
    if (window.Joisst) {
      const newTheme = window.Joisst.user.theme === 'dark' ? 'light' : 'dark';
      window.Joisst.updateUser({ theme: newTheme });
    }
  };

  const login = () => {
    if (window.Joisst) {
      window.Joisst.redirectToRegister();
    }
  };

  const logout = () => {
    if (window.Joisst) {
      if (typeof window.Joisst.logout === 'function') {
        window.Joisst.logout();
      } else {
        window.location.href = 'https://api.joisst.com/api/auth?action=logout';
      }
    }
  };

  const refreshSession = async () => {
    if (window.Joisst) {
      await window.Joisst.syncLocalUser();
    }
  };

  const acceptCookies = async () => {};

  const mappedUser: LocalUser = {
    id: joisstUser.userId || joisstUser.id || 'anon',
    maskedEmail: joisstUser.isAnonymous ? 'guest@joisst.com' : 'user@joisst.com',
    isAdmin: !!joisstUser.isAdmin,
    language: (joisstUser.language || 'en') as Language,
    referralCode: joisstUser.referralCode || '',
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString(),
    cookieConsent: true,
  };

  const mappedApiData: JoisstApiUser = {
    userId: joisstUser.userId || joisstUser.id || 'anon',
    email: joisstUser.email || (joisstUser.isAnonymous ? 'guest@joisst.com' : 'user@joisst.com'),
    hearts: typeof joisstUser.hearts === 'number' ? joisstUser.hearts : 1,
    brains: typeof joisstUser.brains === 'number' ? joisstUser.brains : 0,
    isAdmin: !!joisstUser.isAdmin,
    language: joisstUser.language || 'en',
    currency: 'USD',
    referralCode: joisstUser.referralCode || '',
    theme: joisstUser.theme || 'light',
    cookieConsent: true,
  };

  const value: SessionContextValue = {
    user: mappedUser,
    apiData: mappedApiData,
    isLoading: false,
    isAuthenticated: !joisstUser.isAnonymous,
    language: (joisstUser.language || 'en') as Language,
    themeNotice,
    debugMessage: joisstUser.debugMessage,
    refreshSession,
    login,
    logout,
    setLanguage,
    toggleTheme,
    acceptCookies,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used within a SessionProvider');
  return context;
};